import { useState } from 'react';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { GenericProperties } from '../../FlowDesigner/ActionPanel/GenericProperties';
import { VariableScope } from '../../FlowDesigner/Models/VariableScopes/VariableScope';
import useUserInterfaceMetadata from '../Hooks/useUserInterfaceMetadata';
import { ComponentTypes } from '../Models/ComponentTypes';
import { ParameterTypes } from '@@common';
import { ViewModel } from '../Models/ViewModel';
import { filterTreeManagedParameters } from '../Utils/ComponentTreeUtils';
import DialogInput from './Inputs/DialogInput';
import LayoutInput from './Inputs/LayoutInput';
import MenuInput from './Inputs/MenuInput';

type ViewPropertiesProps = {
	view: ViewModel;
	onViewChanged: (view: ViewModel) => void;
	variableScope?: VariableScope;
};

export function ViewProperties({ view, onViewChanged, variableScope }: ViewPropertiesProps) {
	const [pendingPublicViewId, setPendingPublicViewId] = useState<string>();
	const { componentByName } = useUserInterfaceMetadata();
	const parameters = filterTreeManagedParameters(componentByName.get(ComponentTypes.View)?.parameters ?? []);
	const handleViewChanged = (updatedView: ViewModel) => {
		if (view.RequiresAuthentication === true && updatedView.RequiresAuthentication !== true) {
			setPendingPublicViewId(view.Id);
			return;
		}
		onViewChanged(updatedView);
	};

	return (
		<>
			<GenericProperties<ViewModel> parameters={parameters} model={view} id={view.Id} onModelChanged={handleViewChanged} variableScope={variableScope}>
				{(parameterMetadata, value, onValueChange) => {
					if (parameterMetadata.typeCustom === ParameterTypes.ViewMenu) {
						return <MenuInput menu={value} onMenuChange={onValueChange} />;
					}
					if (parameterMetadata.typeCustom === ParameterTypes.ViewDialog) {
						return <DialogInput dialog={value} onDialogChange={onValueChange} />;
					}
					if (parameterMetadata.typeCustom === ParameterTypes.Layout) {
						return <LayoutInput layout={value} onLayoutChange={onValueChange} />;
					}
					return null; // Fall back to default PropertyValueInput handling
				}}
			</GenericProperties>
			<ConfirmDialog
				open={pendingPublicViewId === view.Id && view.RequiresAuthentication === true}
				title="Make this view public?"
				message="Turning off Requires Authentication makes this view publicly available. Anyone who can access the app without signing in can see any data the view displays to them. Make sure that data is safe to share publicly."
				confirmText="Make view public"
				confirmColor="warning"
				onCancel={() => setPendingPublicViewId(undefined)}
				onConfirm={() => {
					setPendingPublicViewId(undefined);
					if (pendingPublicViewId === view.Id && view.RequiresAuthentication === true) {
						onViewChanged({ ...view, RequiresAuthentication: false });
					}
				}}
			/>
		</>
	);
}
